import { fromUnixTime, isFuture, isPast, isToday } from 'date-fns'

import { eventStatus } from '@/constants'
import i18n from '@/i18n'
import {
  dateRelativeTranslation,
  dateTranslation,
} from '@/i18n/dateTranslation'
import {
  EventType,
  eventTypes,
  MediaContentType,
  YouTubeContentType,
  VideoSourceType,
} from '@/types'
import { getTimeFormat } from '@/utils/utils'

export const getEventStatus = (event?: Nullable<EventType>) => {
  const start = event?.startTime
  const end = event?.endTime
  const startTime = start && fromUnixTime(start)
  const endTime = end && fromUnixTime(end)
  if (!startTime || !endTime) {
    return null
  }
  return isPast(startTime) && isFuture(endTime)
    ? eventStatus.IS_LIVE
    : isFuture(endTime)
    ? eventStatus.IS_FUTURE
    : eventStatus.IS_PAST
}

const formatDateTime = (
  startTime?: Nullable<Date> | number,
  endTime?: Nullable<Date> | number,
) => {
  if (!startTime || !endTime) {
    return ''
  }
  const timeFormat = getTimeFormat()
  const date = dateRelativeTranslation(startTime, 'dd MMM').toUpperCase()
  const time = dateTranslation(startTime, timeFormat)
  return date + ' • ' + time
}

// TODO: move to sdk utils
export const getBadges = (
  isAccessible: boolean,
  isPPVEvent: boolean,
  isPremiumChannel: boolean,
  isWatchable: boolean,
  event?: Nullable<EventType>,
) => {
  const isBlockedPremiumChannel =
    isWatchable && isPremiumChannel && !isAccessible
  const isBlockedPPVEvent = isWatchable && isPPVEvent && !isAccessible
  const isBlockedPromotionEvent =
    isBlockedPPVEvent &&
    event?.ppvInfo?.some(({ walletId }) => walletId?.startsWith('promotion'))

  const isPurchasedPPVEvent =
    isWatchable &&
    isPPVEvent &&
    isAccessible &&
    !!event?.userRights?.reasons?.find(({ type }) => type === 'Ppv')
  const isSubscribedPremiumChannel =
    isWatchable &&
    isPremiumChannel &&
    isAccessible &&
    (!event ||
      !!event?.userRights?.reasons?.find(({ type }) => type === 'Subscription'))

  const start = event?.startTime
  const end = event?.endTime

  const startTime = start && fromUnixTime(start)
  const endTime = end && fromUnixTime(end)

  const isLiveNow = Boolean(
    event?.live &&
      startTime &&
      endTime &&
      isPast(startTime) &&
      isFuture(endTime),
  )
  const isFutureEvent = Boolean(startTime && isFuture(startTime))
  const isPastEvent = Boolean(endTime && isPast(endTime))

  const isPastChannelEvent = isPastEvent && event?.type === eventTypes.CHANNEL

  const timeLabel = isLiveNow
    ? i18n.t('live')
    : isFutureEvent
    ? formatDateTime(startTime, endTime)
    : isPastChannelEvent
    ? i18n.t('eventOver')
    : ''

  const isTodayEvent = !!startTime && isToday(startTime)

  return {
    isLiveNow,
    isTodayEvent,
    isPastEvent,
    timeLabel,
    isBlockedPremiumChannel,
    isBlockedPPVEvent,
    isBlockedPromotionEvent,
    isSubscribedPremiumChannel,
    isPurchasedPPVEvent,
  }
}

// TODO: move to sdk utils
export const checkIsEventPlayable = (
  event?: Nullable<EventType>,
): event is EventType => {
  const isEventYoutube = event?.type === eventTypes.YOUTUBE
  const isEventVod = event?.type === eventTypes.VOD
  const isEventDeepLink = event?.type === eventTypes.DEEP_LINK

  const currentEventStatus = getEventStatus(event)

  const isLiveNow = currentEventStatus === eventStatus.IS_LIVE
  const isPastEvent = currentEventStatus === eventStatus.IS_PAST

  const isNWExpEvent = event?.nativewaves?.eventId
  const isValidReplay = isEventVod && isPastEvent
  const isValidStream = !isEventVod && isLiveNow
  const canWatchInApp =
    isEventYoutube || isNWExpEvent || isValidReplay || isValidStream
  return Boolean(!isEventDeepLink && canWatchInApp && event?.watchable)
}

export const getMediaVideo = (
  event: EventType,
  contentType?: eventTypes,
  sources?: VideoSourceType[],
): MediaContentType => ({
  id: event.id,
  name: event.name,
  contentType,
  sources,
  adSettings: event.adSettings,
  partnerIds: event.partnerIds,
})
export const getYouTubeVideo = (event: EventType): YouTubeContentType => ({
  id: event.id,
  name: event.name,
  description: event.description,
  partnerIds: event.partnerIds,
})
